import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";

export default function ProjectCards({
  plants,
  isEstimation,
  aimsSendingId,
  onEdit,
  onSendToAIMS,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const targetLabel = isEstimation ? "EPCM" : "AIMS";

  const filteredPlants = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return plants;
    return plants.filter((plant) => {
      const haystack = [
        plant.name,
        plant.description,
        plant.client?.name,
        plant.client?.prefix,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [plants, search]);

  return (
    <Box>
      <TextField
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search projects..."
        size="small"
        sx={{ mb: 2, maxWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="disabled" />
            </InputAdornment>
          ),
        }}
      />

      {filteredPlants.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No projects found.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredPlants.map((plant) => {
            const isAimsSent = Boolean(plant.aimsSent);
            const isSending = aimsSendingId === plant.id;

            return (
              <Grid key={plant.id} item xs={12} sm={6} md={4} lg={3}>
                <Card
                  raised
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0px 6px 5px rgba(182, 188, 218, 0.2)",
                    transition: "box-shadow 0.15s ease",
                    "&:hover": {
                      boxShadow: "0px 6px 14px rgba(182, 188, 218, 0.55)",
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() =>
                      navigate(
                        `/plant/${plant.id}/open${
                          isEstimation ? "?mode=estimation" : ""
                        }`,
                      )
                    }
                    sx={{ flexGrow: 1, alignItems: "flex-start" }}
                  >
                    <CardContent>
                      <Typography variant="h6" component="div" noWrap>
                        {plant.name || "Untitled"}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          mb: 1.5,
                          minHeight: "2.6em",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {plant.description || "No description"}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 0.75,
                        }}
                      >
                        {(plant.client?.name || plant.client?.prefix) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={plant.client?.name || plant.client?.prefix}
                          />
                        )}
                        {plant.sharepointLink && (
                          <Tooltip title="Has directory link">
                            <FolderIcon fontSize="small" color="primary" />
                          </Tooltip>
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>

                  <Divider />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Chip
                      size="small"
                      label={
                        isSending
                          ? "Sending..."
                          : isAimsSent
                            ? `Sent To ${targetLabel}`
                            : `Send To ${targetLabel}`
                      }
                      icon={isAimsSent ? <CheckCircleIcon /> : <AddIcon />}
                      variant={isAimsSent ? "filled" : "outlined"}
                      color={isAimsSent ? "success" : "primary"}
                      disabled={isAimsSent || isSending}
                      onClick={() => onSendToAIMS(plant.id)}
                    />
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onEdit(plant)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
